import { ClientLoggingService } from '../../../shared/client-logging.service';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  Output,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  ChatMessage,
  SelectedTextContext,
  TimelineEvent,
  ToolCall,
  TOOL_CALL_STATUS,
} from '../../../models/chat.models';
import { TextFocusService } from '../../../shared/text-focus.service';
import { FirebaseAuthService } from '../../../shared/firebase-auth.service';
import { marked } from 'marked';
import Prism from 'prismjs';
import 'prismjs/components/prism-json';
import DOMPurify from 'dompurify';
import { timer } from 'rxjs';
import { share } from 'rxjs/operators';
import { AttachmentThumbnailsComponent } from '../attachment-thumbnails/attachment-thumbnails.component';

/**
 * Component for displaying a single chat message.
 *
 * Features:
 * - Renders user and assistant messages differently
 * - Shows thinking content in collapsible section
 * - Displays tool calls with expandable details
 * - Markdown rendering for message content
 */
@Component({
  selector: 'app-chat-message',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatTooltipModule,
    AttachmentThumbnailsComponent,
  ],
  templateUrl: './chat-message.component.html',
  styleUrls: ['./chat-message.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessageComponent implements OnInit, OnChanges, OnDestroy {
  auth = inject(FirebaseAuthService);
  private sanitizer = inject(DomSanitizer);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  @Input() message!: ChatMessage;
  @Input() showAvatar: boolean = true;
  @Input() retryDisabled: boolean = false;
  @Output() retryRequested = new EventEmitter<string>();
  @Output() reportErrorRequested = new EventEmitter<string>();

  userPhotoUrl$ = this.auth.user$;
  renderedContent: SafeHtml = '';
  renderedThinking: SafeHtml = '';
  isStreaming = false;
  /** Shared clock tick that refreshes elapsed durations in the template. */
  elapsedTimer$ = timer(0, 1000).pipe(share());

  retry(): void {
    this.retryRequested.emit(this.message.messageId);
  }

  reportError(): void {
    this.reportErrorRequested.emit(this.message.messageId);
  }

  openErrorAction(): void {
    if (this.message.errorAction === 'OPEN_BILLING') {
      void this.router.navigate(['/billing']);
    }
  }

  private userPanelStates = new Map<number, boolean>();

  private previousContent = '';
  private previousThinkingContent = '';
  private timelineMarkdownCache = new Map<number, { text: string; html: SafeHtml }>();
  private toolCallResultSet = new Set<string>();

  private fullHtml = '';
  private revealedChars = 0;
  private revealRafId: number | null = null;
  private lastRenderTime = 0;
  private static readonly BASE_CHARS_PER_MS = 0.3;
  private static readonly CATCHUP_MS = 400;
  private static readonly VOICE_BASE_CHARS_PER_MS = 0.06;
  private static readonly VOICE_CATCHUP_MS = 1200;
  private revealFrameCount = 0;

  private static readonly VOID_ELEMENTS = new Set([
    'area',
    'base',
    'br',
    'col',
    'embed',
    'hr',
    'img',
    'input',
    'link',
    'meta',
    'source',
    'track',
    'wbr',
  ]);

  private static twLog(...args: any[]) {
    console.debug('[Typewriter]', ...args);
    ClientLoggingService.log('DEBUG', '[Typewriter]', ...args);
  }

  constructor() {
    // Configure marked for compact rendering
    marked.setOptions({
      breaks: false, // Don't convert single newlines to <br> (prevents extra spacing)
      gfm: true,
    });
  }

  ngOnInit() {
    this.isStreaming = !this.isUser && !!this.message.streaming;
    this.renderContent();
    if (this.isStreaming) {
      this.revealedChars = 0;
      this.scheduleReveal();
    } else {
      this.revealedChars = this.countVisibleChars(this.fullHtml);
    }
  }

  private changeSeq = 0;

  ngOnChanges() {
    const seq = ++this.changeSeq;
    const prevStreaming = this.isStreaming;
    this.isStreaming = !this.isUser && !!this.message.streaming;
    const contentLen = this.message.content?.length ?? 0;
    const prevVisibleChars = this.countVisibleChars(this.fullHtml);

    ChatMessageComponent.twLog(
      `ngOnChanges #${seq}`,
      `role=${this.isUser ? 'USER' : 'ASSISTANT'}`,
      `streaming=${this.message.streaming}`,
      `contentLen=${contentLen}`,
      `isStreaming=${prevStreaming}->${this.isStreaming}`,
      `revealed=${this.revealedChars}/${prevVisibleChars}`,
      `rafActive=${!!this.revealRafId}`
    );

    this.renderContent();

    const newVisibleChars = this.countVisibleChars(this.fullHtml);
    if (newVisibleChars !== prevVisibleChars) {
      ChatMessageComponent.twLog(
        `ngOnChanges #${seq} content parsed`,
        `visibleChars=${prevVisibleChars}->${newVisibleChars}`,
        `delta=+${newVisibleChars - prevVisibleChars}`
      );
    }

    this.scheduleReveal();
    this.renderThinking();
    this.invalidateChangedToolCalls();
  }

  ngOnDestroy() {
    this.cancelReveal();
  }

  /**
   * Parse the full markdown content to HTML. Called once per content change,
   * NOT on every animation frame. The full HTML is then progressively revealed
   * by the typewriter.
   */
  private renderContent() {
    const content = this.message.content || '';
    if (!content) {
      this.fullHtml = '';
      this.renderedContent = '';
      return;
    }
    if (this.isUser) {
      this.fullHtml = '';
      this.renderedContent = this.sanitizer.sanitize(1, content) || '';
      return;
    }
    this.fullHtml = marked.parse(content) as string;
    if (!this.isStreaming) {
      const cleanHtml = DOMPurify.sanitize(this.fullHtml);
      this.renderedContent = this.sanitizer.bypassSecurityTrustHtml(cleanHtml);
    }
  }

  /**
   * Decide whether to animate or render immediately.
   * Non-streaming messages show the full HTML instantly.
   * Streaming messages use the HTML-aware typewriter reveal.
   */
  private scheduleReveal() {
    if (!this.fullHtml || this.isUser) {
      this.cancelReveal();
      this.revealedChars = this.countVisibleChars(this.fullHtml);
      return;
    }

    if (!this.isStreaming) {
      const totalVisible = this.countVisibleChars(this.fullHtml);
      if (this.revealedChars > 0 && this.revealedChars < totalVisible && !this.revealRafId) {
        ChatMessageComponent.twLog(
          'stream ended, finishing animation',
          `revealed=${this.revealedChars}/${totalVisible}`
        );
        this.lastRenderTime = performance.now();
        this.revealRafId = requestAnimationFrame(ts => this.revealTick(ts));
        return;
      }
      if (this.revealRafId) return;
      this.revealedChars = totalVisible;
      const cleanHtml = DOMPurify.sanitize(this.fullHtml);
      this.renderedContent = this.sanitizer.bypassSecurityTrustHtml(cleanHtml);
      return;
    }

    const totalVisible = this.countVisibleChars(this.fullHtml);

    if (totalVisible < this.revealedChars) {
      ChatMessageComponent.twLog('reset (content shrank)', `old=${this.revealedChars}`, `new=0`);
      this.revealedChars = 0;
    }

    if (totalVisible > this.revealedChars && !this.revealRafId) {
      this.revealFrameCount = 0;
      this.lastRenderTime = performance.now();
      this.revealRafId = requestAnimationFrame(ts => this.revealTick(ts));
      ChatMessageComponent.twLog(
        'animation started',
        `revealed=${this.revealedChars}`,
        `target=${totalVisible}`,
        `delta=${totalVisible - this.revealedChars}`
      );
    }

    if (totalVisible === this.revealedChars) {
      const cleanHtml = DOMPurify.sanitize(this.fullHtml);
      this.renderedContent = this.sanitizer.bypassSecurityTrustHtml(cleanHtml);
    }
  }

  private revealTick(timestamp: number) {
    this.revealRafId = null;
    this.revealFrameCount++;
    const totalVisible = this.countVisibleChars(this.fullHtml);

    if (this.revealedChars >= totalVisible) {
      ChatMessageComponent.twLog('animation complete', `frames=${this.revealFrameCount}`, `chars=${totalVisible}`);
      const cleanHtml = DOMPurify.sanitize(this.fullHtml);
      this.renderedContent = this.sanitizer.bypassSecurityTrustHtml(cleanHtml);
      return;
    }

    const elapsed = timestamp - this.lastRenderTime;
    const backlog = totalVisible - this.revealedChars;
    const isVoice = this.message.modality === 'voice';
    const baseRate = isVoice ? ChatMessageComponent.VOICE_BASE_CHARS_PER_MS : ChatMessageComponent.BASE_CHARS_PER_MS;
    const catchupMs = isVoice ? ChatMessageComponent.VOICE_CATCHUP_MS : ChatMessageComponent.CATCHUP_MS;
    const adaptiveRate = Math.max(baseRate, backlog / catchupMs);
    const charsToAdd = Math.max(1, Math.floor(elapsed * adaptiveRate));
    const before = this.revealedChars;
    this.revealedChars = Math.min(totalVisible, this.revealedChars + charsToAdd);

    if (this.revealFrameCount <= 5 || this.revealFrameCount % 30 === 0) {
      ChatMessageComponent.twLog(
        'tick',
        `frame=${this.revealFrameCount}`,
        `elapsed=${elapsed.toFixed(1)}ms`,
        `rate=${adaptiveRate.toFixed(2)}c/ms`,
        `+${charsToAdd}chars`,
        `${before}->${this.revealedChars}/${totalVisible}`,
        `backlog=${totalVisible - this.revealedChars}`
      );
    }

    const truncated = this.truncateHtml(this.fullHtml, this.revealedChars);
    const cleanHtml = DOMPurify.sanitize(truncated);
    this.renderedContent = this.sanitizer.bypassSecurityTrustHtml(cleanHtml);
    this.lastRenderTime = timestamp;
    this.cdr.markForCheck();

    if (this.revealedChars < totalVisible) {
      this.revealRafId = requestAnimationFrame(ts => this.revealTick(ts));
    } else {
      ChatMessageComponent.twLog('caught up', `frames=${this.revealFrameCount}`, `chars=${totalVisible}`);
    }
  }

  private cancelReveal() {
    if (this.revealRafId) {
      cancelAnimationFrame(this.revealRafId);
      this.revealRafId = null;
    }
  }

  /**
   * Count visible (non-tag, non-entity-overhead) characters in an HTML string.
   * HTML tags contribute 0, HTML entities (e.g. &amp;) contribute 1.
   */
  private countVisibleChars(html: string): number {
    let count = 0;
    let i = 0;
    while (i < html.length) {
      if (html[i] === '<') {
        const close = html.indexOf('>', i);
        i = close !== -1 ? close + 1 : i + 1;
      } else if (html[i] === '&') {
        const semi = html.indexOf(';', i);
        i = semi !== -1 && semi - i < 10 ? semi + 1 : i + 1;
        count++;
      } else {
        count++;
        i++;
      }
    }
    return count;
  }

  /**
   * Truncate an HTML string to show only `visibleLimit` visible characters.
   * Tags are emitted whole (so the DOM structure stays consistent across frames),
   * and any open tags at the cut point are auto-closed.
   */
  private truncateHtml(html: string, visibleLimit: number): string {
    let visible = 0;
    let i = 0;
    const openTags: string[] = [];

    while (i < html.length && visible < visibleLimit) {
      if (html[i] === '<') {
        const close = html.indexOf('>', i);
        if (close === -1) break;
        const tag = html.substring(i, close + 1);

        if (tag.startsWith('</')) {
          openTags.pop();
        } else if (!tag.endsWith('/>')) {
          const match = tag.match(/^<(\w+)/);
          if (match && !ChatMessageComponent.VOID_ELEMENTS.has(match[1].toLowerCase())) {
            openTags.push(match[1]);
          }
        }
        i = close + 1;
      } else if (html[i] === '&') {
        const semi = html.indexOf(';', i);
        if (semi !== -1 && semi - i < 10) {
          i = semi + 1;
        } else {
          i++;
        }
        visible++;
      } else {
        visible++;
        i++;
      }
    }

    let result = html.substring(0, i);
    for (let j = openTags.length - 1; j >= 0; j--) {
      result += `</${openTags[j]}>`;
    }
    return result;
  }

  private renderThinking() {
    const thinkingContent = this.message.metadata?.thinkingContent || '';
    if (!thinkingContent) {
      this.renderedThinking = '';
      this.previousThinkingContent = '';
      return;
    }

    if (thinkingContent === this.previousThinkingContent) return;
    this.previousThinkingContent = thinkingContent;

    const html = marked.parse(thinkingContent) as string;
    const cleanHtml = DOMPurify.sanitize(html);
    this.renderedThinking = this.sanitizer.bypassSecurityTrustHtml(cleanHtml);
  }

  /**
   * Cached markdown render for timeline events.
   * Only re-renders when event content actually changes.
   */
  renderMarkdown(text: string, index: number): SafeHtml {
    if (!text) return '';
    const entry = this.timelineMarkdownCache.get(index);
    if (entry && entry.text === text) return entry.html;

    const html = marked.parse(text) as string;
    const cleanHtml = DOMPurify.sanitize(html);
    const result = this.sanitizer.bypassSecurityTrustHtml(cleanHtml);
    this.timelineMarkdownCache.set(index, { text, html: result });
    return result;
  }

  /**
   * Only invalidate highlight cache entries for tool calls whose data changed.
   */
  private invalidateChangedToolCalls() {
    const toolCalls = this.message.metadata?.toolCalls;
    if (!toolCalls) return;

    for (const tc of toolCalls) {
      const hadResult = this.toolCallResultSet.has(tc.operationId);
      const hasResult = tc.result !== undefined || tc.error != null;
      if (hasResult && !hadResult) {
        this.highlightCache.delete(tc.operationId);
        this.toolCallResultSet.add(tc.operationId);
      }
    }
  }

  /**
   * Compute effective expanded state for a thinking panel.
   * Thinking panels stay collapsed by default, including during streaming, so a
   * programmatic expansion cannot be persisted as a user choice by Material's
   * opened event.
   */
  isPanelExpanded(index: number, event: TimelineEvent): boolean {
    if (event.type !== 'thinking') return false;
    return this.userPanelStates.get(index) ?? false;
  }

  /**
   * Record user's manual panel toggle so it is preserved across re-renders.
   */
  onPanelToggle(index: number, expanded: boolean) {
    this.userPanelStates.set(index, expanded);
  }

  get isUser(): boolean {
    return this.message.role === 'USER';
  }

  get avatar(): string {
    return this.isUser ? 'person' : 'smart_toy';
  }

  /**
   * Copy message content to clipboard
   */
  copyContent() {
    navigator.clipboard.writeText(this.message.content);
  }

  /**
   * Get icon for tool based on tool name
   */
  getToolIcon(toolName: string): string {
    if (toolName.includes('Plan')) return 'description';
    if (toolName.includes('Compliance')) return 'verified';
    if (toolName.includes('Search')) return 'search';
    if (toolName.includes('Code')) return 'code';
    return 'api';
  }

  /**
   * Format tool name for display
   */
  formatToolName(toolName: string): string {
    // Convert "ArchitecturalPlanService_GetArchitecturalPlan" to "Get Architectural Plan"
    const parts = toolName.split('_');
    if (parts.length > 1) {
      return parts[1].replace(/([A-Z])/g, ' $1').trim();
    }
    return toolName;
  }

  isToolCallInProgress(toolCall: ToolCall): boolean {
    return !toolCall.result && !toolCall.error;
  }

  getStatusClass(toolCall: ToolCall): string {
    if (this.isToolCallInProgress(toolCall)) return 'status-pending';
    if (toolCall.status === TOOL_CALL_STATUS.NOT_FOUND) return 'status-not-found';
    if (toolCall.status === TOOL_CALL_STATUS.TIMED_OUT) return 'status-error';
    if (toolCall.status === TOOL_CALL_STATUS.FAILED || toolCall.error) return 'status-error';
    return 'status-success';
  }

  getStatusLabel(toolCall: ToolCall): string {
    if (this.isToolCallInProgress(toolCall)) return '⏳ Executing';
    if (toolCall.status === TOOL_CALL_STATUS.NOT_FOUND) return '⊘ Not Found';
    if (toolCall.status === TOOL_CALL_STATUS.TIMED_OUT) return '⏱ Timed out';
    if (toolCall.status === TOOL_CALL_STATUS.FAILED || toolCall.error) return '✗ Failed';
    return '✓ Success';
  }

  formatElapsed(event: TimelineEvent, _tick?: number | null): string {
    if (event.timestamp == null) return '';
    const elapsed = Math.max(0, Date.now() - event.timestamp);
    return `${(elapsed / 1000).toFixed(1)}s`;
  }

  private static readonly TRUNCATION_LENGTH = 500;
  private expandedResults = new Set<string>();

  /**
   * Per-render-cycle cache of highlighted HTML, keyed by operationId.
   * Avoids repeated JSON.stringify + Prism.highlight when the template
   * calls highlightParams(), getDisplayResult(), and isResultTruncated()
   * for the same tool call in one change detection pass.
   * Cleared in ngOnChanges() so incremental updates (e.g., result arriving
   * after initial params-only render) are re-highlighted.
   */
  private highlightCache = new Map<string, HighlightedToolCall>();

  /**
   * Sanitize Prism-highlighted HTML through DOMPurify to prevent XSS,
   * then mark as trusted for Angular's [innerHTML] binding.
   */
  private sanitizeHighlightedHtml(html: string): SafeHtml {
    const clean = DOMPurify.sanitize(html, { ALLOWED_TAGS: ['span'], ALLOWED_ATTR: ['class'] });
    return this.sanitizer.bypassSecurityTrustHtml(clean);
  }

  /**
   * Get or compute the cached highlight data for a tool call.
   * Computes JSON.stringify and Prism.highlight once per operationId,
   * avoiding expensive re-computation on every change detection cycle.
   */
  private getHighlightData(toolCall: ToolCall): HighlightedToolCall {
    const id = toolCall.operationId;
    const cached = this.highlightCache.get(id);
    if (cached) return cached;

    const maxLen = ChatMessageComponent.TRUNCATION_LENGTH;
    const paramsJson = toolCall.parameters ? JSON.stringify(toolCall.parameters, null, 2) : '{}';
    const resultJson = toolCall.result ? JSON.stringify(toolCall.result, null, 2) : '';

    const highlight = (text: string) => {
      if (!text || typeof text !== 'string') return this.sanitizer.bypassSecurityTrustHtml('');

      // Defensive check: Ensure Prism and the JSON language are available.
      // In production builds, Prism highlights can sometimes fail if the language
      // definition (prismjs/components/prism-json) wasn't properly bundled or initialized.
      const jsonLang = Prism.languages['json'];
      if (!jsonLang) {
        // Fallback to plain text if highlighting fails
        return this.sanitizer.sanitize(1, text) || '';
      }

      try {
        const html = Prism.highlight(text, jsonLang, 'json');
        return this.sanitizeHighlightedHtml(html);
      } catch (e) {
        ClientLoggingService.log('ERROR', '[ChatMessage] Prism highlighting failed:', e);
        return this.sanitizer.sanitize(1, text) || '';
      }
    };

    const data: HighlightedToolCall = {
      paramsHtml: highlight(paramsJson),
      resultTruncatedHtml: resultJson
        ? highlight(resultJson.length > maxLen ? resultJson.substring(0, maxLen) + '\n... (truncated)' : resultJson)
        : null,
      resultFullHtml: resultJson ? highlight(resultJson) : null,
      isTruncatable: resultJson.length > maxLen,
    };
    this.highlightCache.set(id, data);
    return data;
  }

  /** Highlighted parameters HTML for a tool call (cached). */
  highlightParams(toolCall: ToolCall): SafeHtml {
    return this.getHighlightData(toolCall).paramsHtml;
  }

  /** Highlighted result HTML for a tool call, respecting expand/collapse state (cached). */
  getDisplayResult(toolCall: ToolCall): SafeHtml | null {
    const data = this.getHighlightData(toolCall);
    return this.expandedResults.has(toolCall.operationId) ? data.resultFullHtml : data.resultTruncatedHtml;
  }

  /** Whether a tool call result exceeds the truncation threshold (cached). */
  isResultTruncated(toolCall: ToolCall): boolean {
    return this.getHighlightData(toolCall).isTruncatable;
  }

  /** Whether a tool call result is currently expanded. */
  isResultExpanded(toolCall: ToolCall): boolean {
    return this.expandedResults.has(toolCall.operationId);
  }

  /** Toggle inline expansion of a truncated tool call result. */
  toggleFullResult(toolCall: ToolCall) {
    if (this.expandedResults.has(toolCall.operationId)) {
      this.expandedResults.delete(toolCall.operationId);
    } else {
      this.expandedResults.add(toolCall.operationId);
    }
  }

  /**
   * Handle link clicks in message content.
   *
   * Intercepts clicks on internal links to use Angular Router (SPA navigation)
   * while preserving browser features like Ctrl+Click and right-click copy.
   *
   * @param event Mouse click event
   */
  onLinkClick(event: MouseEvent) {
    let target = event.target as HTMLElement;

    // Walk up the DOM tree to find an anchor tag (in case user clicked on child element)
    while (target && target.tagName !== 'A' && target !== event.currentTarget) {
      target = target.parentElement as HTMLElement;
    }

    // Check if we found an anchor tag
    if (target && target.tagName === 'A') {
      const link = target as HTMLAnchorElement;
      const href = link.getAttribute('href');

      // Only intercept internal links (starting with /)
      if (href && href.startsWith('/')) {
        // Allow Ctrl+Click / Cmd+Click to open in new tab (browser default)
        if (event.ctrlKey || event.metaKey) {
          ClientLoggingService.log('INFO', '[ChatMessage] Ctrl+Click detected, allowing browser to open in new tab');
          return; // Let browser handle it
        }

        // Allow middle mouse button click to open in new tab
        if (event.button === 1) {
          ClientLoggingService.log('INFO', '[ChatMessage] Middle click detected, allowing browser to open in new tab');
          return;
        }

        // Prevent default page reload
        event.preventDefault();

        // Use Angular Router for SPA navigation (preserves chat context!)
        ClientLoggingService.log('INFO', '[ChatMessage] Navigating via Angular Router to:', href);
        this.router.navigateByUrl(href);
      } else if (this.isExternalUrl(href)) {
        event.preventDefault();
        window.open(href, '_blank', 'noopener,noreferrer');
      }
    }
  }

  /**
   * Whether a URL points to an external site (absolute http/https).
   * Internal links use relative paths (e.g. `/projects/.../pages/3/preview`)
   * and are routed via Angular Router in {@link onLinkClick}.
   */
  private isExternalUrl(href: string | null): href is string {
    return !!href && (href.startsWith('http://') || href.startsWith('https://'));
  }

  /**
   * Navigates back to the document preview and focuses the cited text selection (Issue #1745).
   */
  navigateToQuote(quote: SelectedTextContext): void {
    if (!quote.projectId) return;
    const path = [
      '/projects',
      quote.projectId,
      'files',
      quote.fileId || '1',
      'pages',
      String(quote.pageNumber || 1),
      'preview',
    ];
    const queryParams: Record<string, string> = {};
    if (quote.selector) {
      queryParams['text'] = TextFocusService.encodeSelector({
        v: 1,
        exact: quote.selector.exact,
        occurrence: quote.selector.occurrence ?? 0,
        prefix: quote.selector.prefix,
        suffix: quote.selector.suffix,
        fingerprint: quote.selector.fingerprint,
      });
    }
    this.router.navigate(path, { queryParams });
  }

  /**
   * Format a duration in ms for display: "Xms" for < 1s, "X.Xs" for >= 1s.
   */
  formatDuration(ms: number): string {
    if (!ms || ms <= 0) return '0ms';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  }

  /**
   * Whether a thinking event is still actively streaming.
   * The message owns the stream lifecycle, while the absence of a duration marks
   * this particular thought as the currently open timeline event.
   */
  isThinkingInProgress(event: TimelineEvent): boolean {
    return event.type === 'thinking' && this.message.streaming === true && event.durationMs == null;
  }

  /**
   * Header text for a thinking expansion panel (excludes duration).
   * Shows the active label while streaming, then the generated summary or a generic label.
   */
  getThinkingHeaderLabel(event: TimelineEvent): string {
    if (this.isThinkingInProgress(event)) {
      return 'Thinking...';
    }
    if (event.summary) {
      return event.summary;
    }
    return 'Thought';
  }

  /**
   * Duration text for a thinking event header (right-aligned badge).
   * Shows live elapsed timer while in progress, final duration when done.
   */
  getThinkingDuration(event: TimelineEvent, _tick?: number | null): string {
    if (this.isThinkingInProgress(event)) {
      return this.formatElapsed(event, _tick);
    }
    if (event.durationMs && event.durationMs > 0) {
      return this.formatDuration(event.durationMs);
    }
    return '';
  }

  /**
   * Extract contextual info from tool call parameters for display in the header.
   * Returns a short string like "Page 5" or "fire exits" derived from well-known
   * parameter names. Works for any tool without needing per-tool rules (Issue #877).
   */
  getToolContext(toolCall: ToolCall): string {
    const p = toolCall.parameters || {};

    for (const extractor of GENERIC_PARAM_EXTRACTORS) {
      const result = extractor(p);
      if (result) return result;
    }

    return toolCall.summary ?? '';
  }

  /** Truncate text and append ellipsis if it exceeds maxLen. */
  private truncateContext(text: string, maxLen: number): string {
    if (text.length <= maxLen) return text;
    return text.substring(0, maxLen) + '…';
  }

  /**
   * Format the message timestamp based on the provided rules:
   * - Today, HH:mm AM/PM
   * - Yesterday, HH:mm AM/PM
   * - Day of week, HH:mm AM/PM (if < 7 days)
   * - Month DD, HH:mm AM/PM (if this year)
   * - YYYY, Month DD, HH:mm AM/PM (if prior year)
   */
  formatMessageTimestamp(date: Date | any): string {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    if (isNaN(d.getTime())) return '';

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const targetDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());

    const diffTime = today.getTime() - targetDate.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    const timeStr = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

    if (diffDays === 0) {
      return `Today, ${timeStr}`;
    } else if (diffDays === 1) {
      return `Yesterday, ${timeStr}`;
    } else if (diffDays > 1 && diffDays < 7) {
      const dayStr = d.toLocaleDateString(undefined, { weekday: 'long' });
      return `${dayStr}, ${timeStr}`;
    } else if (d.getFullYear() === now.getFullYear()) {
      const dateStr = d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
      return `${dateStr}, ${timeStr}`;
    } else {
      const yearStr = d.getFullYear();
      const dateStr = d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
      return `${yearStr}, ${dateStr}, ${timeStr}`;
    }
  }
}

/** Truncates long text to a specified character limit, appending an ellipsis. */
function truncate(text: string, max: number): string {
  return text.length <= max ? text : text.substring(0, max) + '…';
}

type ParamExtractor = (params: Record<string, any>) => string | null;

/**
 * Generic parameter extractors tried in priority order (Issue #877).
 * Each function inspects well-known parameter names and returns a short
 * context string, or null to defer to the next extractor. Any new tool
 * with one of these parameter names gets context automatically.
 */
const GENERIC_PARAM_EXTRACTORS: ParamExtractor[] = [
  p => (p['pageNumber'] != null ? `Page ${p['pageNumber']}` : null),
  p => (typeof p['query'] === 'string' && p['query'] ? truncate(p['query'], 35) : null),
  p => (typeof p['searchQuery'] === 'string' && p['searchQuery'] ? truncate(p['searchQuery'], 35) : null),
  p => (typeof p['sectionId'] === 'string' && p['sectionId'] ? `§${truncate(p['sectionId'], 30)}` : null),
  p => (typeof p['iccBookId'] === 'string' && p['iccBookId'] ? `Book ${truncate(p['iccBookId'], 20)}` : null),
];

/** Pre-computed highlighted HTML for a single tool call. */
interface HighlightedToolCall {
  paramsHtml: SafeHtml;
  resultTruncatedHtml: SafeHtml | null;
  resultFullHtml: SafeHtml | null;
  isTruncatable: boolean;
}
